import React, { useState, useEffect } from 'react';
import { BookOpen, Download, Brain, Zap, FileText, Settings, ChevronDown, Search, AlertCircle, Play, CheckCircle, Loader2 } from 'lucide-react';
import DeepSeekProgressModal from './DeepSeekProgressModal';
import deepSeekMathsCardApiService from '../services/deepseekMathsCardApi';

const MathsCardGenerator = () => {
  const [mathTopic, setMathTopic] = useState('');
  const [generatedCard, setGeneratedCard] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressData, setProgressData] = useState({
    stage: 'connecting',
    message: '正在連接 DeepSeek API...',
    tokensUsed: 0,
    timeElapsed: 0,
    cost: 0
  });
  const [selectedPreset, setSelectedPreset] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Preset math topics for quick selection
  const presetTopics = [
    {
      id: 'color-coding',
      title: '顏色編碼學習法',
      description: '如何使用顏色Color Coding的方法去教導同學學習數學，在解題時，以顏色分別圈出數式或相關數學資訊以便有系統地分解題目及給出解答。最好有一套固定規範應用在往後的數學題目。留意，不是以單一數字或變數以顏色分別那磨簡單。是應以數式運算時的Grouping去用顏色區分。例子：因式分解（a)部分時，把得出的數式放出(b)部分的運算中，使學生知道他們的演化時的關係。例子：如圓形性質時，把相同的圓週角以相同的顏色演示，方便學生明白。例子：在變分題目中，先假設好公式但加入k1及k 2的變分常數。當加入數據組（通常2組，化兩條聯立方程，求出了k1及k2變分常數。然後重寫有實質數字的公式。此3個例子就是一個Color Block。方便學生記住此種思考技巧。注意：數學算式必須在網頁上正常顯示。最少3題高中數學作為例子，適合香港高中數學程度，記住不可過深或過淺'
    },
    {
      id: 'quadratic-equations',
      title: '二次方程式',
      description: '二次方程式的解法，包括因式分解法、配方法和公式法。包含實際應用題目和詳細解題步驟。'
    },
    {
      id: 'trigonometry',
      title: '三角學',
      description: '三角函數的基本概念，包括正弦、餘弦、正切函數，以及它們在幾何問題中的應用。'
    },
    {
      id: 'calculus',
      title: '微積分基礎',
      description: '導數和積分的基本概念，包括求導法則、積分技巧和在實際問題中的應用。'
    },
    {
      id: 'probability',
      title: '概率與統計',
      description: '概率的基本概念，包括條件概率、獨立事件，以及統計學中的均值、中位數、眾數等概念。'
    },
    {
      id: 'geometry',
      title: '幾何學',
      description: '平面幾何和立體幾何的基本概念，包括圓的性質、三角形的性質、四邊形的性質等。'
    },
    {
      id: 'algebra',
      title: '代數學',
      description: '代數運算、方程求解、不等式、函數等基本概念和技巧。'
    },
    {
      id: 'sequences',
      title: '數列與級數',
      description: '等差數列、等比數列的概念，以及數列求和的方法和應用。'
    }
  ];

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    setMathTopic(preset.description);
    setIsDropdownOpen(false);
  };

  const handleGenerateCard = async () => {
    if (!mathTopic.trim()) {
      alert('請輸入數學主題');
      return;
    }

    setIsGenerating(true);
    setShowProgressModal(true);
    setProgressData({
      stage: 'connecting',
      message: '正在連接 DeepSeek API...',
      tokensUsed: 0,
      timeElapsed: 0,
      cost: 0
    });

    try {
      const result = await deepSeekMathsCardApiService.generateMathsCard(
        mathTopic,
        (progress) => {
          setProgressData(progress);
        }
      );

      if (result.success) {
        setGeneratedCard(result);
        setProgressData(prev => ({
          ...prev,
          stage: 'completed',
          message: '數學學習卡片生成完成！',
          cost: result.metadata.costIncurred
        }));
      } else {
        throw new Error('生成失敗');
      }
    } catch (error) {
      console.error('Error generating MathsCard:', error);
      setProgressData(prev => ({
        ...prev,
        stage: 'error',
        message: '生成失敗',
        error: error.message
      }));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadCard = () => {
    if (!generatedCard) return;

    // Create a blob with the HTML content
    const blob = new Blob([generatedCard.htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Create a download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `數學學習卡片_${generatedCard.metadata.topic}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenCard = () => {
    if (!generatedCard) return;

    // Open the card in a new window
    const newWindow = window.open('', '_blank');
    newWindow.document.write(generatedCard.htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <h1 className="text-4xl font-bold text-gray-800">數學學習卡片生成器</h1>
          <div className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
            <Brain className="w-4 h-4" />
            <span>AI 生成</span>
          </div>
        </div>
        <p className="text-xl text-gray-600">使用 DeepSeek AI 生成精美的數學學習卡片</p>
      </div>

      {/* Input Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            數學主題輸入
          </h3>
        </div>

        {/* Preset Topics Dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            快速選擇主題
          </label>
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2 border border-purple-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <span className={selectedPreset ? 'text-gray-900' : 'text-gray-500'}>
                {selectedPreset ? presetTopics.find(p => p.id === selectedPreset)?.title : '選擇預設主題...'}
              </span>
              <ChevronDown className={`w-4 h-4 text-purple-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Preset Dropdown */}
            {isDropdownOpen && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-purple-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="py-1">
                  {presetTopics.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset)}
                      className="w-full px-3 py-2 text-left hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                    >
                      <div className="font-medium text-gray-900">{preset.title}</div>
                      <div className="text-xs text-purple-600">{preset.description.substring(0, 60)}...</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Topic Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            數學主題描述
          </label>
          <textarea
            value={mathTopic}
            onChange={(e) => setMathTopic(e.target.value)}
            placeholder="請詳細描述您想要學習的數學主題，例如：二次方程式的解法、三角函數的應用等..."
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerateCard}
          disabled={isGenerating || !mathTopic.trim()}
          className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              生成中...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5 mr-2" />
              生成數學學習卡片
            </>
          )}
        </button>
      </div>

      {/* Generated Card Display */}
      {generatedCard && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
              生成的數學學習卡片
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleOpenCard}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                <span>預覽</span>
              </button>
              <button
                onClick={handleDownloadCard}
                className="flex items-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>下載</span>
              </button>
            </div>
          </div>

          {/* Card Preview */}
          <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="text-sm text-gray-600 mb-2">
              <strong>主題：</strong>{generatedCard.metadata.topic}
            </div>
            <div className="text-sm text-gray-600 mb-2">
              <strong>生成時間：</strong>{generatedCard.metadata.generationTime}秒
            </div>
            <div className="text-sm text-gray-600 mb-2">
              <strong>Token 使用量：</strong>{generatedCard.metadata.tokensUsed}
            </div>
            <div className="text-sm text-gray-600">
              <strong>成本：</strong>¥{generatedCard.metadata.costIncurred.toFixed(4)}
            </div>
          </div>

          {/* HTML Content Preview */}
          <div className="mt-4">
            <details className="border border-gray-200 rounded-lg">
              <summary className="px-4 py-2 bg-gray-100 cursor-pointer font-medium">
                HTML 內容預覽
              </summary>
              <div className="p-4 bg-gray-50 max-h-96 overflow-y-auto">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                  {generatedCard.htmlContent.substring(0, 1000)}...
                </pre>
              </div>
            </details>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">使用說明</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
            <BookOpen className="w-5 h-5 text-blue-500" />
            <div>
              <p className="font-semibold text-blue-800">輸入主題</p>
              <p className="text-sm text-blue-600">詳細描述您想要學習的數學主題</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
            <Zap className="w-5 h-5 text-green-500" />
            <div>
              <p className="font-semibold text-green-800">AI 生成</p>
              <p className="text-sm text-green-600">DeepSeek AI 生成精美學習卡片</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
            <Download className="w-5 h-5 text-purple-500" />
            <div>
              <p className="font-semibold text-purple-800">下載使用</p>
              <p className="text-sm text-purple-600">下載 HTML 文件或直接預覽</p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
          <h4 className="font-semibold text-yellow-800 mb-2">功能特色：</h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• 繁體中文顯示，適合香港學生使用</li>
            <li>• 多個 SVG 圖形展示，視覺化學習</li>
            <li>• 內建 PDF 導出功能</li>
            <li>• 響應式設計，支援手機和電腦</li>
            <li>• 現代化視覺效果，提升學習體驗</li>
            <li>• 數學算式正常顯示，不使用 LaTeX 格式</li>
          </ul>
        </div>
      </div>

      {/* DeepSeek Progress Modal */}
      <DeepSeekProgressModal
        isOpen={showProgressModal}
        progress={progressData}
        onClose={() => setShowProgressModal(false)}
        title="數學學習卡片生成中"
      />
    </div>
  );
};

export default MathsCardGenerator;
